import { Fragment } from 'react'
import Linkify from 'linkify-react'
import Link from 'next/link'
import type { MentionUser } from '@/components/message-input/mention-list'

const escapeRegExp = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

const MAX_LENGTH = 30

function tooLongTruncate(value: string) {
  if (value.length > MAX_LENGTH) {
    return `${value.slice(0, MAX_LENGTH)}...`
  }
  return value
}

type Props = {
  text: string
  className?: string
}

type Nl2BrWithLinkProps = Props & {
  truncateUrl?: boolean
}

/**
 * Line break to br tag
 * @param {string} text
 */
export default function Nl2Br({ text }: Props) {
  const texts = text.replace('\\n', '\n').split('\n')
  return (
    <>
      {
        texts.map((line, index) => (
          <Fragment key={index}>
            {line}
            {
              index < texts.length - 1 && (
                <br />
              )
            }
          </Fragment>
        ))
      }
    </>
  )
}

export function Nl2BrWithLink({ text, truncateUrl = true }: Nl2BrWithLinkProps) {
  const texts = text.replace('\\n', '\n').split('\n')
  return (
    <>
      {texts.map((line, index) => (
        <Fragment key={index}>
          <Linkify
            options={{
              defaultProtocol: 'https',
              className: 'link break-all',
              target: { url: '_blank' },
              rel: { url: 'noopener noreferrer' },
              format: { url: truncateUrl ? tooLongTruncate : (value: string) => value },
              validate: {
                email: false,
              },
            }}
          >
            {line}
          </Linkify>
          {index < texts.length - 1 && <br />}
        </Fragment>
      ))}
    </>
  )
}

export function Nl2Paragraphs({ text = '', className }: Props) {
  const texts = text.replace('\\n', '\n').split(/\n\n+/)
  return (
    <>
      {texts.map((line, index) => (
        <p key={index} className={className}>
          <Nl2Br text={line} />
        </p>
      ))}
    </>
  )
}


function prepareText(text: string, members: MentionUser[] = []) {
  // メンバー名（@名前）を最優先で検出するための一覧。長い名前を先にマッチさせる
  const mentionNames = members
    .map((member) => member.name)
    .filter((name) => name.length > 0)
    .sort((a, b) => b.length - a.length)
  const usernameByName = new Map(members.map((member) => [ member.name, member.username ]))

  // メンバー名メンション（@名前）に加え、従来の @username / #タグ も引き続き検出する
  const memberAlt = mentionNames.map(escapeRegExp).join('|')
  const pattern = memberAlt
    ? new RegExp(`@(?:${memberAlt})|[@#][a-z0-9_.]+`, 'gi')
    : /[@#][a-z0-9_.]+/gi

  let match = pattern.exec(text)
  if (match == null) {
    return <Nl2BrWithLink text={text} />
  }

  const result = []
  let lastIndex = 0
  let key = 0

  while (match != null) {
    const token = match[0]
    if (match.index > lastIndex) {
      result.push(<Nl2BrWithLink key={key} text={text.slice(lastIndex, match.index)} />)
      key += 1
    }

    const name = token.slice(1)
    const username = usernameByName.get(name)

    if (token[0] === '@' && username != null) {
      // メンバー名メンション → ユーザーページへのリンク + ピル表示
      result.push(
        <Link key={key} href={`/user/${username}`} className="mention mx-1 inline-block">
          {token}
        </Link>,
      )
    }
    else if (token[0] === '@') {
      result.push(
        <Link key={key} href={`/user/${name}`} className="link mx-1 inline-block">
          {token}
        </Link>,
      )
    }
    else {
      result.push(
        <Link
          key={key}
          href={`/search?q=${encodeURIComponent(token)}`}
          className="mx-1 inline-block text-gray hover:underline"
        >
          {token}
        </Link>,
      )
    }
    key += 1
    lastIndex = match.index + token.length
    match = pattern.exec(text)
  }

  if (lastIndex < text.length) {
    result.push(<Nl2BrWithLink key={key} text={text.slice(lastIndex)} />)
  }
  return result
}

export function makeParagraphs(text: string, members: MentionUser[] = []) {
  return text.split(/\n\n+/).map((item, index) => (
    <p key={index} className="mb-4">
      {prepareText(item, members)}
    </p>
  ))
}
